<!-- @format -->

<template>
	<draggable
		:list="widgetDatas"
		item-key="code"
		class="el-row widget-form"
		v-bind="{
			group: {
				name: 'all',
				put: canDragInContainer,
			},
			ghostClass: 'ghost',
			animation: 300,
			handle: '.widget-drag',
		}"
		@add="onDragAdd"
		@update="onDragUpdate"
	>
		<template #item="{ element }: { element: FieldsType }">
			<el-col
				:span="+element.pcProps.cols"
				class="widget-form-col"
				:field-type="element.category === 'container' ? 'container' : 'field'"
				@click.stop="setSelectField(element)"
			>
				<widget-select-field :item="element" :can-delete="canDelete" :can-sort="canSort" @delete="onDelete(element)">
					<!-- 容器 -->
					<template v-if="element.category === 'container'">
						<component :is="getWidgetName(element.type, name)" :item="element">
							<template #default="scope: { data: Container, prop: any }">
								<widget-form
									v-if="scope?.data"
									v-model:widget-datas="scope.data.children"
									:parent-node="element"
									:form-datas="formDatas"
									:can-delete="canDelete"
									:can-sort="canSort"
									:init-code="initCode"
									:is-mobile="isMobile"
									:hidden="element.pcProps.hidden"
									:can-drag-in="canDragIn"
									:is-container-to-container="isContainerToContainer"
									:is-field-to-container="isFieldToContainer"
									v-bind="scope.prop"
									@on-delete="emit('on-delete', $event)"
									@on-select-callback="emit('on-select-callback')"
								/>
								<!-- tab容器的children从他自己组件传出 -->
								<widget-form
									v-else
									v-model:widget-datas="element.children"
									:parent-node="element"
									:form-datas="formDatas"
									:can-delete="canDelete"
									:can-sort="canSort"
									:init-code="initCode"
									:is-mobile="isMobile"
									:hidden="element.pcProps.hidden"
									:can-drag-in="canDragIn"
									:is-container-to-container="isContainerToContainer"
									:is-field-to-container="isFieldToContainer"
									v-bind="scope.prop"
									@on-delete="emit('on-delete', $event)"
									@on-select-callback="emit('on-select-callback')"
								/>
							</template>
						</component>
					</template>
					<!-- 字段 -->
					<template v-else>
						<widget-form-item
							:model-value="formDatas[element.pcProps.code!]"
							class="widget-form-item"
							:type="element.type"
							:pc-props="element.pcProps"
							:item="element"
							:hidden="hidden"
						/>

						<div class="widget-form-item-disabled" />
					</template>
				</widget-select-field>
			</el-col>
		</template>
	</draggable>
</template>

<script setup lang="ts">
	// import { v4 as uuidv4 } from 'uuid';
	import { uuid as uuidv4 } from '@power/utils';
	import { getWidgetName } from '../../components/widget-field';
	import { useDragStoreWithOut } from '../../store';
	import { useHistoryStoreWithOut } from '../../store/history';
	import { deepClone } from '../../utils/util';
	import WidgetFormItem from '../../components/widget-form-item/index.vue';
	import WidgetSelectField from './widget-select-field.vue';
	import type { FieldsType, Container } from '../../types';
	import { draggable } from '@/components';

	interface Props {
		widgetDatas: FieldsType[];
		formDatas: Record<string, any>;
		parentNode?: Container;
		isFieldToContainer?: boolean;
		isContainerToContainer?: boolean;
		canDelete?: boolean | ((a: any) => boolean);
		canSort?: boolean | ((a: any) => boolean);
		canDragIn?: boolean | ((a: any) => boolean);
		initCode?: boolean | ((a: any) => string);
		isMobile?: boolean;
		hidden?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		canDelete: true,
		canSort: true,
		canDragIn: true,
		initCode: true,
	});

	const emit = defineEmits(['update:widgetDatas', 'on-delete', 'on-select-callback']);

	const name = inject<string>('name');

	const dragStore = useDragStoreWithOut(name);
	const historyStore = useHistoryStoreWithOut(name);

	const widgetDatas = computed({
		get() {
			return props.widgetDatas;
		},
		set(val) {
			emit('update:widgetDatas', val);
		},
	});

	/*
    没有parentNode则为画布
    分三种name: all、container、field

    isFieldToContainer (字段 => 容器)
    画布 container
    容器 all

    !isContainerToContainer (容器 !=> 容器)
    画布 all
    容器 field

    isFieldToContainer && !isContainerToContainer (字段 => 容器 && 容器 !=> 容器)
    画布 container
    容器 field
 */
	const canDragInContainer = (_, __, el) => {
		const fieldType = el.getAttribute('field-type');

		if (props.isFieldToContainer && !props.isContainerToContainer) {
			return !props.parentNode ? fieldType === 'container' : props.parentNode ? fieldType === 'field' : true;
		} else if (props.isFieldToContainer) {
			return !props.parentNode ? fieldType === 'container' : true;
		} else if (!props.isContainerToContainer) {
			return props.parentNode ? fieldType === 'field' : true;
		} else {
			return true;
		}
	};

	const setCode = (objs) => {
		return objs.map((obj) => {
			//流程需要根据code建数据库字段，不能带中划线
			const prefixCode = obj.type.replace('-', '_');
			const code = `${prefixCode}_${uuidv4().toString().trim().split('-')[0]}`;
			if (obj.children) {
				obj.children = setCode(obj.children);
			}
			return obj.code
				? obj
				: {
						...obj,
						code,
						pcProps: {
							...obj.pcProps,
							...(Object.hasOwnProperty.call(obj.pcProps, 'code')
								? {
										code:
											typeof props.initCode === 'boolean' && props.initCode
												? obj.pcProps.code || code
												: typeof props.initCode === 'function'
												? props.initCode(obj.pcProps.code || code)
												: obj.pcProps.code,
								  }
								: {}),
						},
				  };
		});
	};

	const onDragAdd = (evt) => {
		const newIndex = evt.newIndex;
		const newWidgetDatas: FieldsType[] = deepClone(widgetDatas.value);
		let obj = newWidgetDatas[newIndex];

		// 删除icon属性，对drag来说没用
		delete obj.icon;

		// 移动端cols固定为24
		if (props.isMobile) {
			obj.pcProps.cols = '24';
		}

		// 添加code属性
		obj = setCode([obj])[0];

		newWidgetDatas[newIndex] = obj;

		// 给widgetDatas赋值
		widgetDatas.value = newWidgetDatas;

		// 选中新增的组件
		setSelectField(obj);

		// 添加历史记录
		historyStore.push(dragStore.widgetDatas);
	};

	const onDragUpdate = () => {
		// 添加历史记录
		historyStore.push(dragStore.widgetDatas);
	};

	const onDelete = (evt: FieldsType) => {
		const idx = widgetDatas.value.findIndex((itm) => itm.code === evt.code);
		// 执行父组件事件
		emit('on-delete', deepClone(widgetDatas.value[idx]));

		widgetDatas.value.splice(idx, 1);

		// 选中新增的组件
		const newSelectField = widgetDatas.value.length
			? idx === 0
				? widgetDatas.value[idx]
				: widgetDatas.value[idx - 1]
			: props.parentNode;
		newSelectField && setSelectField(newSelectField);

		// 添加历史记录
		historyStore.push(dragStore.widgetDatas);
	};

	const setSelectField = (itm: FieldsType) => {
		emit('on-select-callback');
		dragStore.setState({ selectField: itm });
	};
</script>

<style scoped lang="scss">
	.widget-form {
		height: 100%;
		width: 100%;
		align-content: flex-start;
		padding-right: 3px;
		padding-left: 3px;
		padding-bottom: 20px;

		.widget-form-col {
			position: relative;
			margin-bottom: 6px;
			padding-right: 3px;
			padding-left: 3px;

			.widget-form-item {
				margin: 10px 10px 15px 10px;
			}

			.widget-form-item-disabled {
				position: absolute;
				height: 100%;
				width: 100%;
				top: 0;
				left: 0;
				z-index: 1;
			}
		}
	}
</style>
